import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Divider,
  LinearProgress,
  Card,
  CardContent,
  Alert,
  Avatar,
  AvatarGroup,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  PushPin as PinIcon,
  PushPinOutlined as UnpinIcon,
  Description as FileIcon,
  NewReleases as NewIcon,
  GetApp as DownloadIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  PlayArrow as StartIcon,
  Stop as StopIcon,
  AccessTime as ClockIcon,
  TrendingUp as TrendingIcon,
} from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';

const categoryColors = {
  'Plans': 'primary',
  'Change Order': 'warning',
  'Notes': 'default',
  'Photos': 'info',
};

export default function ProjectDetail({ project, onBack }) {
  const [files, setFiles] = useState(project.files || []);
  const [isClocked, setIsClocked] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (isClocked && startTime) {
      intervalRef.current = setInterval(() => {
        const now = Date.now();
        const elapsed = Math.floor((now - startTime) / 1000);
        setElapsedTime(elapsed);
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isClocked, startTime]);

  const handleClockIn = () => {
    const now = Date.now();
    setStartTime(now);
    setIsClocked(true);
    setElapsedTime(0);
    enqueueSnackbar(`Clocked in to ${project.jobNumber}`, { variant: 'success' });
  };

  const handleClockOut = () => {
    setIsClocked(false);
    const hours = (elapsedTime / 3600).toFixed(2);
    enqueueSnackbar(`Clocked out - ${hours} hours logged`, { variant: 'info' });
    setElapsedTime(0);
    setStartTime(null);
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePinToggle = (fileId) => {
    setFiles(prevFiles => {
      const updated = prevFiles.map(file =>
        file.id === fileId ? { ...file, isPinned: !file.isPinned } : file
      );
      // Sort: pinned files first
      const sorted = updated.sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        return 0;
      });

      // Show success message
      const file = updated.find(f => f.id === fileId);
      enqueueSnackbar(
        file.isPinned ? 'File pinned to top!' : 'File unpinned',
        { variant: 'success' }
      );

      return sorted;
    });
  };

  const pinnedFiles = files.filter(f => f.isPinned);
  const regularFiles = files.filter(f => !f.isPinned);

  const hoursPercentage = project.budgetHours
    ? (project.actualHours / project.budgetHours) * 100
    : 0;

  return (
    <Box>
      {/* Back Button */}
      <Box sx={{ mb: 2 }}>
        <Button startIcon={<BackIcon />} onClick={onBack} size="small">
          Back to Dashboard
        </Button>
      </Box>

      {/* Clean Project Header */}
      <Box sx={{ bgcolor: 'grey.50', p: 3, mb: 3, borderBottom: 2, borderColor: 'primary.main' }}>
        {/* Line 1: Job Identity */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {project.jobNumber}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {project.name}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Chip
              label={project.pm}
              sx={{
                bgcolor: project.pmColor,
                color: 'white'
              }}
            />
            <Chip
              label={project.status}
              color="success"
            />
            {project.hasLift && (
              <Chip
                label={`Lift: ${project.liftHeight}`}
                color="warning"
                icon={<WarningIcon />}
              />
            )}
          </Box>
        </Box>

        {/* Line 2: Key Metrics */}
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary" display="block">
              TIMELINE
            </Typography>
            <Typography variant="body1">
              {project.startDate} - {project.endDate}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary" display="block">
              BUDGET
            </Typography>
            <Box>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: hoursPercentage >= 65 ? 'bold' : 'normal',
                  color: hoursPercentage >= 85 ? 'error.main' : hoursPercentage >= 65 ? 'warning.main' : 'text.primary'
                }}
              >
                {project.actualHours}/{project.budgetHours} hrs ({Math.round(hoursPercentage)}%)
              </Typography>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, hoursPercentage)}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  mt: 0.5,
                  bgcolor: 'grey.300',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: hoursPercentage >= 85 ? 'error.main' : hoursPercentage >= 65 ? 'warning.main' : 'primary.main'
                  }
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={8} md={4}>
            <Typography variant="caption" color="text.secondary" display="block">
              TEAM ON-SITE
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {isClocked ? (
                <>
                  <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 32, height: 32, fontSize: '0.875rem' } }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>Y</Avatar>
                    <Avatar sx={{ bgcolor: '#9c27b0' }}>M</Avatar>
                    <Avatar sx={{ bgcolor: '#607d8b' }}>C</Avatar>
                    <Avatar sx={{ bgcolor: '#e91e63' }}>D</Avatar>
                  </AvatarGroup>
                  <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                    {formatTime(elapsedTime)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    • Updated 2 min ago
                  </Typography>
                </>
              ) : (
                <>
                  <AvatarGroup max={4} sx={{ '& .MuiAvatar-root': { width: 32, height: 32, fontSize: '0.875rem' } }}>
                    <Avatar sx={{ bgcolor: '#9c27b0' }}>M</Avatar>
                    <Avatar sx={{ bgcolor: '#607d8b' }}>C</Avatar>
                    <Avatar sx={{ bgcolor: '#e91e63' }}>D</Avatar>
                  </AvatarGroup>
                  <Typography variant="caption" color="text.secondary">
                    3 installers • Updated 2 min ago
                  </Typography>
                </>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} sm={4} md={2}>
            {isClocked ? (
              <Button
                fullWidth
                variant="contained"
                color="error"
                onClick={handleClockOut}
                startIcon={<StopIcon />}
              >
                Clock Out
              </Button>
            ) : (
              <Button
                fullWidth
                variant="contained"
                color="success"
                onClick={handleClockIn}
                startIcon={<StartIcon />}
              >
                Clock In
              </Button>
            )}
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={3}>
        {/* Full Width - Files */}
        <Grid item xs={12}>
          <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h5" fontWeight="bold">
                Project Files
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  icon={<PinIcon />}
                  label={`${pinnedFiles.length} Pinned`}
                  size="small"
                  sx={{ bgcolor: 'primary.main', color: 'white' }}
                />
                <Chip
                  label={`${files.length} Total Files`}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Box>

            {/* Success Banner */}
            <Alert
              severity="success"
              sx={{
                mb: 3,
                '& .MuiAlert-message': { width: '100%' }
              }}
            >
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Key Feature
                </Typography>
                <Typography variant="body2">
                  Your installation plans are always pinned at the top. No more scrolling through 87 visits to find critical documents!
                </Typography>
              </Box>
            </Alert>

            {/* Pinned Files Section */}
            {pinnedFiles.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PinIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">
                    Pinned Files (Always Visible)
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {pinnedFiles.map(file => (
                    <Card
                      key={file.id}
                      variant="outlined"
                      sx={{
                        bgcolor: 'primary.50',
                        borderColor: 'primary.200',
                        '&:hover': {
                          bgcolor: 'primary.100',
                        }
                      }}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
                            <FileIcon sx={{ color: 'primary.main', mt: 0.5 }} />
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography variant="body1" fontWeight="medium">
                                  {file.name}
                                </Typography>
                                {file.version && (
                                  <Chip label={`v${file.version}`} size="small" sx={{ height: 20 }} />
                                )}
                                {file.isNew && <NewIcon color="error" fontSize="small" />}
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                {file.category} • {file.size} • Uploaded by {file.uploadedBy} on {file.uploadedAt}
                                {file.id === 'f1' && (
                                  <Typography component="span" variant="caption" color="text.secondary">
                                    {' '}(4 months ago)
                                  </Typography>
                                )}
                                {file.id === 'f2' && (
                                  <Typography component="span" variant="caption" color="success.main">
                                    {' '}(2 days ago)
                                  </Typography>
                                )}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton size="small" onClick={() => handlePinToggle(file.id)}>
                              <PinIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small">
                              <ViewIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small">
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Regular Files Section */}
            <Box>
              <Typography variant="h6" gutterBottom>
                All Files (Chronological - {regularFiles.length} visits)
              </Typography>

              {/* Show only first 5 regular files */}
              <List sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 1 }}>
                {regularFiles.slice(0, 5).map(file => (
                  <ListItem
                    key={file.id}
                    sx={{
                      bgcolor: 'background.paper',
                      mb: 1,
                      borderRadius: 1,
                      '&:last-child': { mb: 0 }
                    }}
                    secondaryAction={
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => handlePinToggle(file.id)}>
                          <UnpinIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small">
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemText
                      primary={file.name}
                      secondary={
                        <Box>
                          <Typography variant="body2" component="span">
                            Visit {file.visitNumber} • {file.uploadedAt}
                          </Typography>
                          <Typography variant="caption" component="span" color="text.secondary" sx={{ ml: 1 }}>
                            ({file.visitNumber > 80 ? '3 months ago' :
                              file.visitNumber > 60 ? '2 months ago' :
                              file.visitNumber > 40 ? '1 month ago' :
                              file.visitNumber > 20 ? '2 weeks ago' :
                              'Last week'})
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>

              {regularFiles.length > 5 && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Showing 5 of {regularFiles.length} visits
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Jobber Comparison Footer */}
            <Paper sx={{ p: 2, bgcolor: 'info.50', mt: 3, borderLeft: '4px solid', borderColor: 'info.main' }}>
              <Typography variant="body2">
                <strong>In Jobber:</strong> You'd have to scroll through all {regularFiles.length} visit notes
                to find important files. Here, just pin them to keep them always visible!
              </Typography>
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}